import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Chip,
  Paper,
  Typography,
  TextField,
  Divider,
  Slider,
  Alert,
  Button as MuiButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { MainContainer } from '../../../components/ContentWrapper';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { Button } from '../../../components/Button/Button';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { DotLottleLoader } from '../../../components/Loader';
import { TabHeader } from '../../../components/Tabs/TabHeader';
import config from '../../../config';
import axiosInstant from '../../../api';
import {
  adminScoresSettings,
  adminScoresStatus,
  adminScoresRecompute,
  adminScoresDistribution,
  adminAttributes,
} from '../../../api/endpoints';
import {
  WeightAttr,
  WeightMap,
  WeightProfile,
  normalizeWeights,
  rebalance,
} from './constants';
import { AffinityBands, DEFAULT_AFFINITY_BANDS } from './bands';
import { AlgorithmCard } from './AlgorithmCard';
import { AlgorithmExplainerCards } from './AlgorithmExplainerCards';
import { BestMatchDemo } from './BestMatchDemo';
import { HighlightProvider } from './equationPrimitives';
import { AffinityBandsCard } from './AffinityBandsCard';
import { UniversalValuesCard, UniversalValue } from './UniversalValuesCard';
import { ExperienceCurveChart } from './ExperienceCurveChart';
import { ScoresChart, ScoresDistribution } from './ScoresChart';
import AffinityNetwork from './AffinityNetwork';
import AttributeNetwork from './AttributeNetwork';
import { ExpertIQFAQ } from './ExpertIQFAQ';

interface Status {
  lastComputedAt: string | null;
  count: number;
  lastConfigChangedAt?: string | null;
  stale?: boolean;
}

const TABS = [
  { value: 0, text: 'Demo' },
  { value: 1, text: 'Algorithm' },
  { value: 2, text: 'Scores' },
  { value: 3, text: 'Affinity Bands' },
  { value: 4, text: 'Affinity Net' },
  { value: 5, text: 'Attribute Weights' },
  { value: 6, text: 'Attribute Net' },
  { value: 7, text: 'Attribute Settings' },
  { value: 8, text: 'Experience' },
  { value: 9, text: 'FAQ' },
];

const ExpertIQ = () => {
  const [tab, setTab] = useState(0);

  const [tau, setTau] = useState<string>('5');
  const [originalTau, setOriginalTau] = useState<string>('5');
  const [affinityBands, setAffinityBands] = useState<AffinityBands>(DEFAULT_AFFINITY_BANDS);
  const [universalValues, setUniversalValues] = useState<UniversalValue[]>([]);
  const [attrs, setAttrs] = useState<WeightAttr[]>([]);
  // Normalized weights (always sum to 1), keyed by attribute id. `original` is
  // the loaded baseline, used to PATCH only what actually changed.
  const [weights, setWeights] = useState<WeightMap>({});
  const [original, setOriginal] = useState<WeightMap>({});
  // Second weight vector: the Industry lens. Same kernel, same sliders,
  // separate column (attributes.industry_weight). Computed per-request by the
  // backend — unlike Match weights, edits apply immediately with NO recompute.
  const [weightProfile, setWeightProfile] = useState<WeightProfile>('match');
  const [industryWeights, setIndustryWeights] = useState<WeightMap>({});
  const [industryOriginal, setIndustryOriginal] = useState<WeightMap>({});
  const [status, setStatus] = useState<Status>({ lastComputedAt: null, count: 0 });
  const [showLoader, setShowLoader] = useState(false);
  const [savingWeights, setSavingWeights] = useState(false);
  const [savingTau, setSavingTau] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  // Recompute is a full re-score across every expert × product, so it's gated
  // behind a confirm step to prevent an accidental click kicking off a long job.
  const [confirmRecompute, setConfirmRecompute] = useState(false);
  // Set by any tab's Save; cleared by a successful recompute. Surfaces a banner
  // on the Scores tab prompting the admin to apply edits.
  const [scoresStale, setScoresStale] = useState(false);
  const [distribution, setDistribution] = useState<ScoresDistribution | null>(null);
  const [distLoading, setDistLoading] = useState(false);

  const settingsUrl = `${config.API_URL}${adminScoresSettings}`;
  const statusUrl = `${config.API_URL}${adminScoresStatus}`;
  const recomputeUrl = `${config.API_URL}${adminScoresRecompute}`;
  const distributionUrl = `${config.API_URL}${adminScoresDistribution}`;
  const attrUrl = `${config.API_URL}${adminAttributes}`;

  const fetchSettings = async ({ initStale = false }: { initStale?: boolean } = {}) => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        axiosInstant.get(settingsUrl),
        axiosInstant.get(statusUrl),
      ]);
      const loadedTau = String(settingsRes.data?.tau ?? 5);
      setTau(loadedTau);
      setOriginalTau(loadedTau);
      if (settingsRes.data?.affinityBands) setAffinityBands(settingsRes.data.affinityBands);
      setUniversalValues(settingsRes.data?.universalValues ?? []);
      const w: WeightAttr[] = settingsRes.data?.weights ?? [];
      setAttrs(w);
      const normalized = normalizeWeights(w);
      setWeights(normalized);
      setOriginal(normalized);
      const normalizedIndustry = normalizeWeights(w, 'industry_weight');
      setIndustryWeights(normalizedIndustry);
      setIndustryOriginal(normalizedIndustry);
      setStatus(statusRes.data ?? { lastComputedAt: null, count: 0 });
      // On the initial load, seed the stale flag from the server so the chip
      // reflects persisted staleness (settings changed in a prior session or by
      // another admin). In-session saves/recompute manage the flag optimistically.
      if (initStale) setScoresStale(!!statusRes.data?.stale);
    } catch (err) {
      toast.error('Failed to load Expert IQ settings');
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    setShowLoader(true);
    fetchSettings({ initStale: true });
  }, []);

  const fetchDistribution = async () => {
    setDistLoading(true);
    try {
      const res = await axiosInstant.get(distributionUrl);
      setDistribution(res.data ?? null);
    } catch (err) {
      setDistribution(null);
    } finally {
      setDistLoading(false);
    }
  };

  // Lazy-load the (potentially large) score distribution the first time the
  // Scores tab is opened.
  useEffect(() => {
    if (tab === 2 && !distribution && !distLoading) fetchDistribution();
  }, [tab]); // eslint-disable-line

  const sortedAttrs = useMemo(
    () => [...attrs].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    [attrs],
  );

  // The slider panel binds to whichever profile is toggled on.
  const activeWeights = weightProfile === 'match' ? weights : industryWeights;
  const setActiveWeights = weightProfile === 'match' ? setWeights : setIndustryWeights;

  const totalPct = useMemo(
    () => Object.values(activeWeights).reduce((s, v) => s + v, 0) * 100,
    [activeWeights],
  );
  const totalOk = Math.abs(totalPct - 100) < 0.5;

  const matchWeightsDirty = useMemo(
    () => attrs.some((a) => Math.abs((weights[a.id] ?? 0) - (original[a.id] ?? 0)) > 1e-6),
    [attrs, weights, original],
  );
  const industryWeightsDirty = useMemo(
    () => attrs.some((a) => Math.abs((industryWeights[a.id] ?? 0) - (industryOriginal[a.id] ?? 0)) > 1e-6),
    [attrs, industryWeights, industryOriginal],
  );
  // Save persists BOTH profiles' edits so switching the toggle never silently
  // drops the other profile's unsaved changes.
  const weightsDirty = matchWeightsDirty || industryWeightsDirty;
  const tauDirty = tau !== originalTau;

  const resetWeights = () => {
    if (weightProfile === 'match') setWeights(original);
    else setIndustryWeights(industryOriginal);
  };
  const resetTau = () => setTau(originalTau);

  const markStaleAndRefresh = () => {
    setScoresStale(true);
    fetchSettings();
  };

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      // One PATCH per touched attribute, carrying only the fields that
      // changed. default_weight edits mark scores stale (persisted Match
      // scores need a recompute); industry_weight edits do NOT — the lens is
      // computed per-request, so they apply immediately.
      const requests = sortedAttrs
        .map((attr) => {
          const body: Record<string, number> = {};
          if (Math.abs((weights[attr.id] ?? 0) - (original[attr.id] ?? 0)) > 1e-6) {
            body.default_weight = weights[attr.id] ?? 0;
          }
          if (Math.abs((industryWeights[attr.id] ?? 0) - (industryOriginal[attr.id] ?? 0)) > 1e-6) {
            body.industry_weight = industryWeights[attr.id] ?? 0;
          }
          return Object.keys(body).length
            ? axiosInstant.patch(`${attrUrl}/${attr.id}`, body)
            : null;
        })
        .filter(Boolean);
      if (requests.length) await Promise.all(requests);
      if (matchWeightsDirty) {
        toast.success('Weights saved — run a recompute to apply Match weights to scores');
        setScoresStale(true);
      } else {
        toast.success('Industry lens weights saved — live immediately, no recompute needed');
      }
      await fetchSettings();
    } catch (err) {
      toast.error('Failed to save weights');
    } finally {
      setSavingWeights(false);
    }
  };

  const saveTau = async () => {
    const tauNum = Number(tau);
    if (!Number.isFinite(tauNum) || tauNum <= 0) {
      toast.error('Tau must be a positive number');
      return;
    }
    setSavingTau(true);
    try {
      await axiosInstant.patch(settingsUrl, { tau: tauNum });
      toast.success('Experience curve saved — run a recompute to apply it to scores');
      setScoresStale(true);
      await fetchSettings();
    } catch (err) {
      toast.error('Failed to save experience curve');
    } finally {
      setSavingTau(false);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      await axiosInstant.post(recomputeUrl, {});
      toast.success('Recompute enqueued — scores update in a few minutes');
      setScoresStale(false);
      setTimeout(() => {
        fetchSettings();
        fetchDistribution();
      }, 1500);
    } catch (err) {
      toast.error('Failed to trigger recompute');
    } finally {
      setRecomputing(false);
    }
  };

  const lastComputed = status.lastComputedAt
    ? new Date(status.lastComputedAt).toLocaleString()
    : 'never';

  // Flag the Scores tab (value 2) when settings have changed since the last
  // recompute, so staleness is visible from any tab — not just the in-tab banner.
  const tabs = useMemo(
    () =>
      TABS.map((t) =>
        t.value === 2 && scoresStale
          ? {
              ...t,
              badge: (
                <Chip
                  label="Stale"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: '#FBE9E2',
                    color: '#D4725C',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ),
            }
          : t,
      ),
    [scoresStale],
  );

  return (
    <WhiteBackground>
      <MainContainer>
        <Box className="w-full flex flex-wrap justify-between items-center gap-3 pb-2">
          <PageTitle title="Expert IQ" />
        </Box>
        <Typography color="var(--text-2)" className="pb-4" sx={{ maxWidth: 720 }}>
          Tune the expert↔product matching algorithm. Changes are saved as
          defaults but only affect live scores after a recompute, which takes a
          few minutes across all experts and products.
        </Typography>

        <Box className="relative">
          {showLoader && <DotLottleLoader />}
          {!showLoader && (
            <Box className="flex flex-col gap-4">
              <TabHeader
                tabs={tabs}
                selectedTab={tab}
                setSelectedTab={setTab}
                variant="underline"
              />

              {/* 0 — Demo */}
              {tab === 0 && <BestMatchDemo affinityBands={affinityBands} />}

              {/* 1 — Algorithm */}
              {tab === 1 && (
                <HighlightProvider>
                  <Box className="flex flex-col gap-4">
                    <AlgorithmCard />
                    <AlgorithmExplainerCards affinityBands={affinityBands} />
                  </Box>
                </HighlightProvider>
              )}

              {/* 2 — Scores */}
              {tab === 2 && (
                <Paper sx={{ p: 3 }}>
                  {scoresStale && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Settings changed since the last recompute — run a recompute to
                      apply them to live scores.
                    </Alert>
                  )}
                  <Box className="flex flex-wrap items-center justify-between gap-4">
                    <Box>
                      <Typography fontWeight={600}>Score recompute</Typography>
                      <Typography color="var(--text-2)" sx={{ fontSize: 14 }}>
                        Last recomputed: {lastComputed} · {status.count.toLocaleString()} scores
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => setConfirmRecompute(true)}
                      disabled={recomputing}
                      className="!px-5"
                    >
                      <Typography>
                        {recomputing ? 'Enqueuing…' : 'Recompute all scores'}
                      </Typography>
                    </Button>
                  </Box>
                  <ConfirmationDialog
                    open={confirmRecompute}
                    onClose={() => setConfirmRecompute(false)}
                    onConfirm={() => {
                      setConfirmRecompute(false);
                      recompute();
                    }}
                    title="Recompute all scores?"
                    description={[
                      'This re-scores every expert against every product and can take several minutes to finish.',
                      'Current scores stay live until the new ones are ready.',
                    ]}
                    confirmLabel="Recompute"
                    disabled={recomputing}
                  />
                  <Divider sx={{ my: 3 }} />
                  {distLoading && !distribution && (
                    <Box className="flex justify-center py-6">
                      <DotLottleLoader />
                    </Box>
                  )}
                  {distribution && distribution.total > 0 && (
                    <ScoresChart data={distribution} bands={affinityBands} />
                  )}
                  {distribution && distribution.total === 0 && (
                    <Typography color="var(--text-2)" textAlign="center" className="py-4">
                      No scores yet — run a recompute to populate the distribution.
                    </Typography>
                  )}
                </Paper>
              )}

              {/* 3 — Affinity Bands */}
              {tab === 3 && (
                <AffinityBandsCard
                  affinityBands={affinityBands}
                  onAfterSave={markStaleAndRefresh}
                />
              )}

              {/* 4 — Affinity Net */}
              {tab === 4 && <AffinityNetwork onAfterSave={() => setScoresStale(true)} />}

              {/* 5 — Attribute Weights */}
              {tab === 5 && (
                <Paper sx={{ p: 3 }}>
                  <Box className="flex items-center justify-between pb-1">
                    <Box className="flex items-center gap-3">
                      <Typography fontWeight={600}>Attribute weights</Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={weightProfile}
                        onChange={(_, v) => { if (v) setWeightProfile(v as WeightProfile); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25, fontSize: 13 } }}
                      >
                        <ToggleButton value="match">
                          Match weights{matchWeightsDirty ? ' •' : ''}
                        </ToggleButton>
                        <ToggleButton value="industry">
                          Industry lens weights{industryWeightsDirty ? ' •' : ''}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box className="flex items-center gap-3">
                      <MuiButton
                        size="small"
                        variant="outlined"
                        onClick={resetWeights}
                        disabled={weightProfile === 'match' ? !matchWeightsDirty : !industryWeightsDirty}
                      >
                        Reset
                      </MuiButton>
                      <Typography fontWeight={600} sx={{ color: totalOk ? 'var(--up)' : 'var(--down)' }}>
                        Total: {totalPct.toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>
                  {weightProfile === 'match' ? (
                    <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
                      Relative importance of each top-level attribute in the
                      persisted <strong>Match</strong> score — the balanced
                      use-case alignment every surface ranks by. The set always
                      sums to 100% — raising one proportionally lowers the
                      others. Saving Match weights marks scores stale: run a
                      recompute to apply them.
                    </Typography>
                  ) : (
                    <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
                      The <strong>Industry lens</strong> runs the exact same
                      scoring kernel with this second weight vector — nothing
                      else changes. Boost User Tier, Skill / Qualification and
                      Compliance / Risk to reward credentialed{' '}
                      <strong>Industry Experts</strong> (professionals in the
                      product's field — peer-level authority for a core
                      professional audience); damp context attributes
                      (Environment, Use Setting, Frequency…) so heavy product{' '}
                      <em>use</em> alone — the mark of a <strong>Use-Case Expert</strong>{' '}
                      (credible real-life practitioner for adjacent audiences) —
                      doesn't buy authority. This lens drives the I-chips and
                      the Industry/Use-Case badges on brand projects. It is
                      computed live per request: saving applies{' '}
                      <strong>immediately, no recompute needed</strong>.
                    </Typography>
                  )}
                  <Divider sx={{ mb: 2 }} />
                  <Box className="flex flex-col gap-3">
                    {sortedAttrs.map((attr) => {
                      const pct = (activeWeights[attr.id] ?? 0) * 100;
                      return (
                        <Box key={attr.id} className="flex items-center gap-4">
                          <Typography sx={{ width: 220, flexShrink: 0, fontSize: 14 }}>
                            {attr.name}
                          </Typography>
                          <Slider
                            value={pct}
                            min={0}
                            max={100}
                            step={1}
                            aria-label={`${attr.name} ${weightProfile} weight`}
                            onChange={(_, v) =>
                              setActiveWeights((w) =>
                                rebalance(w, attr.id, (Array.isArray(v) ? v[0] : v) / 100),
                              )
                            }
                            sx={{ flex: 1 }}
                          />
                          <Typography sx={{ width: 56, textAlign: 'right', fontSize: 14 }}>
                            {pct.toFixed(1)}%
                          </Typography>
                        </Box>
                      );
                    })}
                    {sortedAttrs.length === 0 && (
                      <Typography className="py-4" color="var(--text-2)" textAlign="center">
                        No top-level attributes found.
                      </Typography>
                    )}
                  </Box>
                  <Box className="flex justify-end pt-4">
                    <Button onClick={saveWeights} disabled={!weightsDirty || savingWeights} className="!px-6">
                      <Typography>{savingWeights ? 'Saving…' : 'Save weights'}</Typography>
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* 6 — Attribute Net */}
              {tab === 6 && <AttributeNetwork onAfterSave={() => setScoresStale(true)} />}

              {/* 7 — Attribute Settings (universal / catch-all values) */}
              {tab === 7 && (
                <UniversalValuesCard
                  universalValues={universalValues}
                  onAfterSave={markStaleAndRefresh}
                />
              )}

              {/* 8 — Experience */}
              {tab === 8 && (
                <Paper sx={{ p: 3 }}>
                  <Box className="flex items-center justify-between pb-1">
                    <Typography fontWeight={600}>Experience curve (tau)</Typography>
                    <MuiButton size="small" variant="outlined" onClick={resetTau} disabled={!tauDirty}>
                      Reset
                    </MuiButton>
                  </Box>
                  <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
                    Years of experience needed to reach ~63% of the experience
                    factor. Lower tau rewards tenure faster. Formula: E = 1 −
                    e^(−years / tau).
                  </Typography>
                  <TextField
                    label="Tau (years)"
                    type="number"
                    inputProps={{ step: 0.5, min: 0.5 }}
                    value={tau}
                    onChange={(e) => setTau(e.target.value)}
                    sx={{ width: 200 }}
                  />
                  <Box sx={{ mt: 3 }}>
                    <ExperienceCurveChart tau={Number(tau)} />
                  </Box>
                  <Box className="flex justify-end pt-4">
                    <Button onClick={saveTau} disabled={!tauDirty || savingTau} className="!px-6">
                      <Typography>{savingTau ? 'Saving…' : 'Save experience'}</Typography>
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* 9 — FAQ */}
              {tab === 9 && <ExpertIQFAQ affinityBands={affinityBands} />}
            </Box>
          )}
        </Box>
      </MainContainer>
    </WhiteBackground>
  );
};

export default ExpertIQ;
